import { Box, Card, CardActionArea, CardContent, Chip, Stack, Typography } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ArticleIcon from "@mui/icons-material/Article";
import { Link as RouterLink } from "react-router";
import { useState } from "react";
import dayjs from "dayjs";
import { proxied } from "../lib/imageProxy.js";
import { decodeHtml } from "../lib/displayHelpers.js";
import type { PublicContent } from "../hooks/useApiQueries.js";
import { SaveButton } from "./SaveButton.js";

function hostnameOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function isImageUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    return /\.(jpe?g|png|webp|gif|svg|avif|heic)(\?.*)?$/.test(
      new URL(url).pathname.toLowerCase(),
    );
  } catch {
    return false;
  }
}

function CardThumbnail({ src, alt }: { src: string; alt: string }) {
  const [loaded, setLoaded] = useState(false);
  const lqipSrc = proxied(src, { lqip: true });
  const fullSrc = proxied(src, { w: 640 });
  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <Box
        component="img"
        src={lqipSrc}
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          filter: "blur(12px)",
          transform: "scale(1.05)",
          opacity: loaded ? 0 : 1,
          transition: "opacity 0.3s",
        }}
      />
      <Box
        component="img"
        src={fullSrc}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.3s, transform 0.3s",
          ".MuiCardActionArea-root:hover &": { transform: "scale(1.04)" },
        }}
      />
    </Box>
  );
}

export function ContentCard({
  item,
  showSaveButton = false,
  onVideoClick,
}: {
  item: PublicContent;
  showSaveButton?: boolean;
  // When provided AND this card is a video with an embed URL, clicking
  // intercepts (preventDefault) and calls onVideoClick instead of navigating
  // to the detail page. Articles always navigate.
  onVideoClick?: (item: PublicContent) => void;
}) {
  const isNewRelease = item.tags.includes("new-release");
  const isArticle = item.type === "ARTICLE";
  const isVideo = item.type === "VIDEO";
  const hasThumb = isImageUrl(item.thumbnailUrl);
  const byline = decodeHtml(item.sourceAuthor ?? hostnameOf(item.sourceUrl));
  const title = decodeHtml(item.title);
  const date = item.publishedAt ? dayjs(item.publishedAt).format("D MMM YYYY") : null;
  const savedItemType = isVideo ? "video" : "article";

  const tractrsTag = item.equipmentTags.find(
    (t) => typeof t.slug === "string" && t.slug.length > 0,
  );
  const tractrsHref = tractrsTag?.slug
    ? `https://tractrs.com/machinery/${tractrsTag.slug}`
    : null;
  const tractrsLabel = tractrsTag
    ? `${tractrsTag.make ?? ""} ${tractrsTag.model ?? ""}`.trim() || "Tractrs"
    : null;

  const typeChip = isNewRelease ? (
    <Chip
      size="small"
      label="New release"
      sx={{ bgcolor: "primary.main", color: "common.black", fontWeight: 700, fontSize: 11 }}
    />
  ) : isVideo ? (
    <Chip
      size="small"
      label="Video"
      sx={{ bgcolor: "rgba(255,176,0,0.15)", color: "primary.main", fontWeight: 700, fontSize: 11, border: "1px solid rgba(255,176,0,0.3)" }}
    />
  ) : (
    <Chip
      size="small"
      label="Article"
      sx={{ bgcolor: "rgba(255,255,255,0.06)", color: "text.secondary", fontSize: 11, border: "1px solid rgba(255,255,255,0.1)" }}
    />
  );

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: "0 0 20px rgba(255,176,0,0.15)" },
      }}
    >
      {showSaveButton && (
        <SaveButton itemType={savedItemType} itemId={item.id} variant="overlay" />
      )}
      <CardActionArea
        component={RouterLink}
        to={`/content/${item.slug}`}
        onClick={(e) => {
          if (onVideoClick && isVideo && (item.embedUrl || item.sourceUrl)) {
            e.preventDefault();
            onVideoClick(item);
          }
        }}
        sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", flex: 1 }}
      >
        {hasThumb || isVideo ? (
          <Box
            sx={{ position: "relative", width: "100%", aspectRatio: "16 / 9", bgcolor: "grey.900", overflow: "hidden" }}
          >
            {hasThumb && (
              <CardThumbnail src={item.thumbnailUrl as string} alt={title} />
            )}
            {isVideo && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0,0,0,0.25)",
                  opacity: hasThumb ? 0 : 1,
                  transition: "opacity 0.15s",
                  ".MuiCardActionArea-root:hover &": { opacity: 1 },
                }}
              >
                <PlayCircleIcon sx={{ fontSize: 52, color: "white", filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.6))" }} />
              </Box>
            )}
            <Box sx={{ position: "absolute", top: 8, left: 8 }}>{typeChip}</Box>
          </Box>
        ) : (
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "rgba(255,255,255,0.02)",
            }}
          >
            <ArticleIcon sx={{ fontSize: 15, color: "text.disabled", flexShrink: 0 }} />
            {typeChip}
            {byline && (
              <Typography
                variant="caption"
                sx={{ ml: "auto", color: "text.disabled", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}
              >
                {byline}
              </Typography>
            )}
          </Box>
        )}

        <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", pt: 1.5, pb: "12px !important" }}>
          {item.featured && (
            <Chip
              size="small"
              label="Featured"
              sx={{ mb: 1, alignSelf: "flex-start", bgcolor: "rgba(255,176,0,0.12)", color: "primary.main", fontWeight: 700, fontSize: 10, border: "1px solid rgba(255,176,0,0.25)" }}
            />
          )}

          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              lineHeight: 1.4,
              fontSize: 14,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              flex: 1,
            }}
          >
            {title}
          </Typography>

          <Stack direction="row" sx={{ mt: 1.25, alignItems: "center", gap: 1 }}>
            {(hasThumb || isVideo) && byline && (
              <Typography
                variant="caption"
                sx={{ color: "text.disabled", fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {byline}
              </Typography>
            )}
            {!byline && isArticle && !hasThumb && (
              <Box sx={{ flex: 1 }} />
            )}
            {date && (
              <Typography variant="caption" sx={{ color: "text.disabled", fontSize: 11, whiteSpace: "nowrap", ml: "auto" }}>
                {date}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>

      {tractrsHref && (
        <Box
          component="a"
          href={tractrsHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.5,
            py: 0.75,
            borderTop: "1px solid",
            borderColor: "divider",
            color: "text.disabled",
            textDecoration: "none",
            fontSize: 11,
            "&:hover": { color: "primary.main", bgcolor: "rgba(255,176,0,0.04)" },
            transition: "color 0.15s, background-color 0.15s",
          }}
        >
          <LaunchIcon sx={{ fontSize: 12 }} />
          <Box component="span" sx={{ fontWeight: 600 }}>{tractrsLabel}</Box>
          <Box component="span" sx={{ opacity: 0.6 }}>on Tractrs</Box>
        </Box>
      )}
    </Card>
  );
}
